/* eslint-disable react-hooks/exhaustive-deps */
'use client'
import { type FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { motion } from 'motion/react'
import type { TypeCmsProductsByCategoryList, TypeLang } from '@/schema'
import { CardProduct } from './CardProduct'
import { defaultGrid, designGrid, getP18, mq, vw } from '@/styles'
import { useFilters, useModal, useTranslator } from '@/hooks'
import { useParams, useRouter, useSearchParams } from 'next/navigation'
import { MODALS } from '@/context'

type Props = {
  data: TypeCmsProductsByCategoryList['products']
}

const getItems = (data: Props['data']) => data.map((item, index) => {
  return (
    <Li key={index}>
      <CardProduct data={item} lazyLoad={index > 6}/>
    </Li>
  )
})

let firstLoad = true

const comingSoonCategories = {
  'led': true,
  'led-strip': true,
  'tiras-led': true,
  'strisce-led': true,
  'rubans-de-le': true
}

export const SectionProductsList: FC<Props> = memo(({ data }) => {
  const { lang, slug }: { lang: TypeLang, slug: string } = useParams()
  const [filteredProducts, setFilteredProducts] = useState(data ?? [])
  const Items = useMemo(() => getItems(filteredProducts), [filteredProducts])
  const { rawFilters, activeFilters, setDisabledFilters } = useFilters()
  const { t } = useTranslator()
  const [comingSoon, setComingSoon] = useState(false)
  const searchParams = useSearchParams()
  const intialQ = searchParams.get('q')
  const advancedSearch = searchParams.get('adv')
  const { toggle } = useModal(MODALS.FILTERS)
  const { replace } = useRouter()

  const getFilteredProducts = useCallback((activeFilters) => {
    if(!activeFilters || Object.keys(activeFilters).length === 0) {
      setFilteredProducts(data)
      return
    }

    const matches = []
    for(const item of data) {
      const itemMatches = Object.keys(activeFilters).every((key) => {
        const filterValues = Object.keys(activeFilters[key])

        const itemFilterKey = item.filters[key]
        if(!itemFilterKey) return false

        const arrayOfItemFilterValues = Object.keys(itemFilterKey?.values)
        return filterValues.some(value => arrayOfItemFilterValues.includes(value))
      })

      if (itemMatches) matches.push(item)
    }

    setFilteredProducts(matches)
  }, [data])

  const getAvailableFilters = useCallback((filteredProducts) => {
    const availableFilters = {}
    for (const item of filteredProducts) {
      const itemFilters = item.filters
      for (const key in itemFilters) {
        if (!availableFilters[key]) availableFilters[key] = {}

        Object.keys(itemFilters[key].values).forEach((value) => {
          availableFilters[key][value] = true
        })
      }
    }

    const filtersToDisable = {}
    for(const rawFilter of rawFilters) {
      const filterKey = rawFilter.slug[lang]
      if(!availableFilters[filterKey]) {
        filtersToDisable[filterKey] = {}
        Object.keys(rawFilter.values).forEach((value) => {
          filtersToDisable[filterKey][value] = true
        })
      } else {
        Object.keys(rawFilter.values).forEach((value) => {
          if (!availableFilters[filterKey][value]) {
            if (!filtersToDisable[filterKey]) filtersToDisable[filterKey] = {}
            filtersToDisable[filterKey][value] = true
          }
        })
      }
    }


    setDisabledFilters(filtersToDisable)
  }, [lang, rawFilters, setDisabledFilters])

  useEffect(() => {
    if (firstLoad) firstLoad = false

    getFilteredProducts(activeFilters)
  }, [activeFilters])

  useEffect(() => {
    getAvailableFilters(filteredProducts)
  }, [filteredProducts])

  useEffect(() => {
    if(slug) setComingSoon(comingSoonCategories[slug])
  }, [slug])

  useEffect(() => {
    if (advancedSearch === 'true') {
      toggle()

      const newParams = new URLSearchParams(searchParams.toString())
      newParams.delete('adv')

      replace(`?${newParams.toString()}`, { scroll: false })
    }
  }, [])


  return (
    <Section className='filters-bar'>
      {intialQ?.length > 0 && <h2>{t('project.search.serch_result')} {intialQ}</h2>}
      {Items.length > 0 ?
        <Ol>
          {Items}
        </Ol>
        :
        <p>{comingSoon ? t('project.grid.coming_soon') : t('project.grid.no_products') }</p>
      }
    </Section>
  )
})

const Section = styled.section`
  margin-bottom: ${vw(160, 'mobile')};
  margin-top: ${vw(10, 'mobile')};

  ${mq.greaterThan('nexus7')} {
    margin-bottom: ${vw(160, 'tablet')};
    margin-top: ${vw(10, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    margin-bottom: ${vw(160, 'desktop')};
    margin-top: ${vw(10, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    margin-bottom: 160px;
    margin-top: 10px;
  }

  p {
    ${getP18()}
    opacity: .5;
    padding: ${vw(120, 'mobile')} ${vw(60, 'mobile')};
    text-align: center;

    ${mq.greaterThan('tablet')}{
      padding: ${vw(120, 'desktop')} ${vw(60, 'desktop')};
    }
  }

  h2 {
    ${getP18()}
    padding: ${vw(30, 'mobile')} ${vw(defaultGrid.mobile.gutter.width /2, 'mobile')};
    text-align: left;


    ${mq.greaterThan('tablet')}{
      padding: ${vw(30, 'desktop')} ${vw(defaultGrid.desktop.gutter.width  , 'desktop')};
    }

    ${mq.greaterThan('desktop')}{
      padding: 30px ${defaultGrid.tablet.gutter.width+15}px;
    }
  }
`

const Ol = styled(motion.ol)`
  ${designGrid({})}
  grid-row-gap: ${vw(20, 'mobile')};

  ${mq.greaterThan('nexus7')} {
    grid-row-gap: ${vw(20, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    grid-row-gap: ${vw(40, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    grid-row-gap: 40px;
  }
`

const Li = styled(motion.li)`
  grid-column: auto / span 3;

  &:nth-child(3n) {
    ${mq.lessThan('tablet')} {
      grid-column: auto / span 6;
    }
  }

  &:nth-child(12n+1),
  &:nth-child(12n+8),
  &:nth-child(12n+9),
  &:nth-child(12n+11) {
    ${mq.greaterThan('tablet')} {
      grid-column: auto / span 6;
    }
  }
`
